import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputLabel from "@mui/material/InputLabel";
import LoadingComponent from "components/LoadingComponent/LoadingComponent.tsx";
import { useToast } from "contexts/ToastContext.tsx";
import Button from "core/components/Button.tsx";
import MotionContainer from "core/components/MotionContainer.tsx";
import Separator from "core/components/Separator.tsx";
import SelectInputField from "core/data-entry/SelectInputField.tsx";
import Layout from "core/layout/Layout.tsx";
import { AnimatePresence, motion } from "framer-motion";
import useIsMobile from "hooks/useIsMobile.ts";
import useService from "hooks/useService.ts";
import { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tenantData } from "../../../components/EmpresasTenant.js";
import TextLinkV2 from "../../../components/TextLink/TextLinkV2.js";
import { useProfile } from "../../../contexts/ProfileContext.tsx";
import RequestControllerRedux from "../../../contexts/RequestController.js";
import Logo from "../../../images/C1010_azul.png";
import { loginUser } from "../../../services/ModuloGerenciador/Usuario.js";
import "./Login.css";

const optionsEmpresa = tenantData.map((item) => ({
	title: item.titulo,
	obj: item.tenant,
}));

enum ContentKeys {
	loaded = "loaded",
	loading = "loading",
}

function Login() {
	const { dadosRequestControllerRedux, setDadosRequestControllerRedux } =
		useContext(RequestControllerRedux);
	const { handleLogin } = useProfile();
	const { showToast } = useToast();

	const navigate = useNavigate();

	const timeoutIdRef = useRef<number | null>(null);

	const clearPendingTimeout = useCallback(() => {
		if (timeoutIdRef.current) {
			clearTimeout(timeoutIdRef.current);
			timeoutIdRef.current = null;
		}
	}, []);

	const scheduleTimeout = useCallback((cb: () => void) => {
		clearPendingTimeout();
		timeoutIdRef.current = window.setTimeout(() => {
			cb();
			timeoutIdRef.current = null;
		}, 200);
	}, [clearPendingTimeout]);

	const [showPassword, setShowPassword] = useState(false);
	const [values, setValues] = useState({
		login: "",
		password: "",
		tenant: "",
		showPassword: false,
	});
	const [contentKey, setContentKey] = useState<string>(ContentKeys.loaded);

	const [, logar] = useService(
		loginUser,
		{
			autoStart: false,
			onData: (result) => {
				const { token, usuario, tabelas } = result.data;
				const tenant = values.tenant;
				if (values.password === "12345678") {
					localStorage.setItem("flagPassword", "true");
				}
				const decodedToken = parseJwt(token);

				const expiracaoTime =
					decodedToken && decodedToken.exp
						? decodedToken.exp * 1000
						: new Date().getTime() + 60 * 60 * 1000;

				localStorage.setItem("expiracao", expiracaoTime.toString());
				localStorage.setItem("token", token);
				localStorage.setItem("tenant", values.tenant);
				localStorage.setItem("usuario", JSON.stringify(usuario));
				handleLogin({
					token,
					id: usuario.idUsuario,
					tabelas,
					tenant,
					usuario,
				});
			},
			onError: (error) => {
				scheduleTimeout(() => {
					if (error?.response?.data) {
						showToast(error.response.data, "error");
						setContentKey(ContentKeys.loaded);
						return;
					}
					showToast("Erro ao realizar login", "error");
					setContentKey(ContentKeys.loaded);
				});
			},
		},
	);

	const handleClickShowPassword = useCallback(() => {
		setShowPassword(!showPassword);
	}, [showPassword]);

	const handleChange = useCallback(
		(prop: keyof typeof values) => (
			event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
		) => {
			setValues((current) => ({
				...current,
				[prop]: event.target.value,
			}));
		},
		[]
	);

	const parseJwt = useCallback((token: string) => {
		try {
			const base64Url = token.split(".")[1];
			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split("")
					.map(function (c) {
						return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
					})
					.join("")
			);
			return JSON.parse(jsonPayload);
		} catch (e) {
			return null;
		}
	}, []);

	const handleLoginClick = useCallback(
		async (event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			setContentKey(ContentKeys.loading);
			scheduleTimeout(() => {
				logar({
					login: values.login,
					password: values.password,
					tenant: values.tenant
				});
			});
		},
		[values, logar]
	);

	const smallDevice = useIsMobile(900);

	useEffect(() => {
		if (localStorage.getItem("tenant")) {
			setValues((current) => ({
				...current,
				tenant: localStorage.getItem("tenant") || "",
			}));
		}
	}, []);

	useEffect(() => {
		if (dadosRequestControllerRedux === "Email Codigo") {
			setDadosRequestControllerRedux("");
			showToast("Seu código foi enviado para o seu email", "success");
		}
		if (dadosRequestControllerRedux === "Senha Alterada") {
			setDadosRequestControllerRedux("");
			showToast("Senha alterada com sucesso!", "success");
		}
		if (dadosRequestControllerRedux === "Salva Senha") {
			setDadosRequestControllerRedux("");
			showToast(
				"Senha salva com sucesso! Aguarde sua solicitação ser aceita.", "success"
			);
		}
	}, [dadosRequestControllerRedux, setDadosRequestControllerRedux, showToast]);

	return (
		<Layout showNavbar={false}>
			<MotionContainer animationKey="LoginScreen">
				<AnimatePresence mode="wait">
					{contentKey === ContentKeys.loaded && (
						<div className="login-container" key={ContentKeys.loaded}>
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.2 }}
								className="card-login-container"
							>
								{smallDevice && (
									<div className="logo-container">
										<motion.img
											initial={{ opacity: 0, scale: 0.8 }}
											animate={{ opacity: 1, scale: 1 }}
											transition={{ duration: 0.15 }}
											src={Logo}
											alt="Logo"
											className="logo-login"
											onLoad={(e) => {
												e.currentTarget.style.opacity = "1";
												e.currentTarget.style.transform = "scale(1)";
											}}
											style={{
												opacity: 0,
												transform: "scale(0.8)",
												transition: "opacity 0.5s, transform 0.5s",
											}}
										/>
									</div>
								)}
								<motion.div
									initial={{ opacity: 0, y: 50 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -50 }}
									transition={{ duration: 0.15 }}
									className="card-login">
									{!smallDevice && (<div className="title-container">
										<h2 className="title-login">Login</h2>
									</div>)}
									<form className="login-form" onSubmit={handleLoginClick}>
										<div className="input">
											<p className="label-login">Usuário</p>
											<input
												type="text"
												className="input-login"
												onChange={handleChange("login")}
												value={values.login}
											/>
											<p className="label-login">Senha</p>
											<div style={{ position: "relative", width: "100%" }}>
												<input
													type={showPassword ? "text" : "password"}
													value={values.password}
													onChange={handleChange("password")}
													className="input-login"
													style={{
														paddingRight: "40px",
														width: "100%",
														boxSizing: "border-box",
													}}
												/>
												<div
													onClick={handleClickShowPassword}
													style={{
														position: "absolute",
														top: "50%",
														right: "10px",
														transform: "translateY(-50%)",
														cursor: "pointer",
													}}
												>
													{showPassword ? <Visibility /> : <VisibilityOff />}
												</div>
											</div>
											<SelectInputField
												label="Empresa"
												value={optionsEmpresa.find((empresa) => values.tenant === empresa.obj)?.title || ""}
												onChange={(e) => {
													setValues((current) => ({
														...current,
														tenant: e.obj,
													}));
													localStorage.setItem("tenant", e.obj);
												}}
												paddingInput={6}
												fontSizeLabel={smallDevice ? "18px" : "22px"}
												onInputChange={() => { }}
												placeholder="Selecione a empresa"
												options={optionsEmpresa}
											/>
										</div>
										<Separator size="xl" />
										<div className="botao">
											<Button
												size="xs"
												disabled={!values.tenant}
												fullWidthContent={smallDevice}
												label="Entrar"
												radius="lg"
												icon="LogIn"
												iconPosition="esquerda"
												iconSize="xl"
												iconTheme="light"
												onClick={() => { }}
											/>
											<div className="cadastrese">
												<p style={{ marginRight: "5px" }}>Ou </p>
												<InputLabel
													className="titulo-select-cadastro"
													onClick={() => {
														navigate("/cadastroUsuario");
													}}
													htmlFor="status"
												>
													<TextLinkV2
														className="decoration"
														texto="Cadastrar-se"
													/>
												</InputLabel>
											</div>
										</div>
									</form>
								</motion.div>
							</motion.div>
						</div>
					)}
					{contentKey === ContentKeys.loading && (
						<div className="content-container" key={ContentKeys.loading}>
							<LoadingComponent />
						</div>
					)}
				</AnimatePresence>
			</MotionContainer>
		</Layout>
	);
}

export default memo(Login);
